import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronRight,
  FileText,
  Pencil,
  ReceiptText,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { useExpenseTransactions } from '@/features/expenses/hooks';
import { ExpenseStatusEditor } from '@/features/expenses/ExpenseStatus';
import type { ExpenseStatus } from '@/types/database';

/**
 * "My expenses" — the cardholder self-service surface. Lists every
 * transaction RLS exposes to the signed-in user (their own card swipes)
 * so they can attach a receipt, add an explanation, pick a QBO category,
 * and submit each one for super_admin approval on the detail page.
 *
 * Super_admins do not see this page in the sidebar — they manage the
 * same workflow from the Transactions page, which has the richer table,
 * the approval queue chip, and Post-to-QBO. See Sidebar.tsx for the
 * visibility predicate.
 *
 * The defaults are tuned for "show me what's blocking me": status
 * filter starts at "Needs attention" (draft + rejected), and the two
 * KPI tiles surface the same two queues from different angles.
 */
export function ExpenseTrackingPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    ExpenseStatus | 'all' | 'needs_attention' | 'posted'
  >('needs_attention');

  const dbStatus: ExpenseStatus | 'all' = useMemo(() => {
    if (statusFilter === 'all' || statusFilter === 'needs_attention') return 'all';
    // "Posted" is a derived state on top of approved expenses — fetch
    // approved and filter by qb_post_status client-side.
    if (statusFilter === 'posted') return 'approved';
    return statusFilter;
  }, [statusFilter]);

  const { data, isLoading } = useExpenseTransactions({ status: dbStatus, limit: 500 });

  const filtered = useMemo(() => {
    const all = data ?? [];
    let rows = all;
    if (statusFilter === 'needs_attention') {
      rows = rows.filter((t) => t.expense_status === 'draft' || t.expense_status === 'rejected');
    }
    if (statusFilter === 'posted') {
      rows = rows.filter((t) => t.qb_post_status === 'posted');
    }
    // "Approved" filter shows only rows whose badge actually reads
    // "Approved" — once QBO accepts a post the badge flips to "Posted"
    // (which has its own filter). Keeps filter ↔ badge semantics in
    // lockstep so the operator's mental model stays consistent.
    if (statusFilter === 'approved') {
      rows = rows.filter((t) => t.qb_post_status !== 'posted');
    }
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(
        (t) =>
          t.description.toLowerCase().includes(s) ||
          t.counterparty?.toLowerCase().includes(s) ||
          t.expense_explanation?.toLowerCase().includes(s) ||
          t.cards?.last_four?.includes(s) ||
          t.cards?.name_on_card?.toLowerCase().includes(s) ||
          t.chart_of_accounts?.name.toLowerCase().includes(s) ||
          false,
      );
    }
    return rows;
  }, [data, search, statusFilter]);

  const pendingApprovalCount = useMemo(
    () => (data ?? []).filter((t) => t.expense_status === 'submitted').length,
    [data],
  );
  const needsAttentionCount = useMemo(
    () =>
      (data ?? []).filter(
        (t) => t.expense_status === 'draft' || t.expense_status === 'rejected',
      ).length,
    [data],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="My expenses"
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SummaryTile
          label="Needs your attention"
          value={needsAttentionCount}
          tone="warning"
        />
        <SummaryTile
          label="Awaiting approval"
          value={pendingApprovalCount}
          tone="info"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              placeholder="Search description, vendor, explanation, card last-4…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="sm:max-w-md"
            />
            <Select
              value={statusFilter}
              onValueChange={(v) =>
                setStatusFilter(
                  v as ExpenseStatus | 'all' | 'needs_attention' | 'posted',
                )
              }
            >
              <SelectTrigger className="sm:w-56">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="needs_attention">Needs attention (draft + rejected)</SelectItem>
                <SelectSeparator />
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="in_qb">Already in QuickBooks</SelectItem>
                <SelectItem value="posted">Posted to QuickBooks</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={ReceiptText}
              title="You don't have any expenses to handle"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Card</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Receipt</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead aria-label="Open" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => {
                  const isPosted = t.qb_post_status === 'posted';
                  return (
                    <TableRow
                      key={t.id}
                      // Posted rows are visually de-emphasised so the eye
                      // skips over them — they're "taken care of" and the
                      // user shouldn't need to act on them. The cursor
                      // stays clickable so the detail page is still
                      // reachable for an audit / drilldown.
                      className={
                        'cursor-pointer ' +
                        (isPosted ? 'bg-bright-moss-dark/5 text-muted-foreground' : '')
                      }
                      onClick={() => {
                        window.location.assign(`/financials/expenses/${t.id}`);
                      }}
                    >
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDate(t.posted_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span
                            className={
                              isPosted
                                ? 'font-medium text-muted-foreground line-through decoration-muted-foreground/40'
                                : 'font-medium text-foreground'
                            }
                          >
                            {t.description}
                          </span>
                          {t.counterparty && (
                            <span className="text-xs text-muted-foreground">{t.counterparty}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {t.cards
                          ? `${t.cards.name_on_card ?? 'Card'} •${t.cards.last_four ?? '????'}`
                          : (t.bank_accounts?.name ?? '—')}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {t.chart_of_accounts ? (
                          <span>{t.chart_of_accounts.name}</span>
                        ) : (
                          <span className="text-muted-foreground/70">Not set</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {t.receipt_path ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-bright-moss-dark">
                            <FileText className="h-3.5 w-3.5" />
                            Attached
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                            <FileText className="h-3.5 w-3.5" />
                            None
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <ExpenseStatusEditor
                          transactionId={t.id}
                          status={t.expense_status}
                          qbPostStatus={t.qb_post_status}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(t.amount_cents)}
                      </TableCell>
                      <TableCell className="w-8 text-right text-muted-foreground">
                        <Link
                          to={`/financials/expenses/${t.id}`}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Open"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'warning' | 'info';
}) {
  const Icon = tone === 'warning' ? AlertTriangle : Pencil;
  return (
    <Card>
      <CardHeader>
        <CardDescription className="flex items-center gap-2">
          <Icon
            className={
              'h-3.5 w-3.5 ' +
              (tone === 'warning' ? 'text-fresh-salmon-dark' : 'text-atmosphere-dark')
            }
          />
          {label}
        </CardDescription>
        <CardTitle className="text-3xl tracking-headline tabular-nums">{value}</CardTitle>
      </CardHeader>
    </Card>
  );
}
